import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { NotificationCode } from '../../models/communication/app-enums.model';
import { NotificationTextPipe } from '../../pipes/notification-text.pipe';

export type NotificationVariant = 'error' | 'hint' | 'notification' | 'success' | 'warning';

@Component({
    selector: 'app-notification',
    imports: [CommonModule],
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss']
})
export class NotificationComponent {
    private static readonly iconMap: Record<NotificationVariant, string> = {
        success: 'icon-success',
        warning: 'icon-warning',
        error: 'icon-error',
        hint: 'icon-hint',
        notification: 'icon-notification'
    };

    private static readonly severityMap: Record<string, NotificationVariant> = {
        warning: 'warning',
        error: 'error',
        success: 'success',
        hint: 'hint',
        info: 'success'
    };

    private static readonly textPipe = new NotificationTextPipe();

    private static readonly defaultTitles: Partial<Record<NotificationCode, string>> = {
        [NotificationCode.AuthenticationFailed]: 'Authentication',
        [NotificationCode.ScannerDisconnected]: 'Warning',
        [NotificationCode.ActionFailed]: 'Action',
        [NotificationCode.CylinderNotReversed]: 'Warning',
        [NotificationCode.BallAndCylinderSameDirection]: 'Warning',
        [NotificationCode.TooFewBallRevolutions]: 'Warning',
        [NotificationCode.CylinderTooFast]: 'Warning',
        [NotificationCode.CylinderStopped]: 'Warning',
        [NotificationCode.BallLost]: 'Warning'
    };

    @Input() code?: NotificationCode;
    @Input() title?: string;
    @Input() description?: string;
    @Input() variant: NotificationVariant = 'notification';
    @Input() showCloseButton = false;
    @Input() context?: unknown;

    @Output() readonly closeClicked = new EventEmitter<void>();

    @Input() set severity(value: string) {
        this.variant = NotificationComponent.mapSeverity(value);
    }

    get classes(): string {
        return `${this.variant} container`;
    }

    get iconClass(): string {
        return `icon ${NotificationComponent.iconMap[this.variant]}`;
    }

    get resolvedTitle(): string {
        if (this.code) {
            return this.title ?? NotificationComponent.getDefaultTitleForCode(this.code);
        }
        return this.title ?? '';
    }

    get resolvedDescription(): string {
        if (this.description) {
            return this.description;
        }
        if (this.code) {
            return NotificationComponent.textPipe.transform(this.code, this.context);
        }
        return '';
    }

    private static mapSeverity(serverSeverity: string): NotificationVariant {
        return NotificationComponent.severityMap[serverSeverity.toLowerCase()] ?? 'notification';
    }

    private static getDefaultTitleForCode(code: NotificationCode): string {
        return NotificationComponent.defaultTitles[code] ?? 'Notification';
    }
}
